import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import * as S from './Collapse.styles';

export const CollapseAppearance = {
  CLASSIC: 'classic',
  MINIMAL: 'minimal',
};

export const Collapse = ({
  disabled,
  main,
  content,
  isFullWidth,
  appearance,
}) => {
  if (!main || !content) {
    return null;
  }

  const [isContentShown, setIsContentShown] = useState(false);
  const [contentHeight, setContentHeight] = useState(null);
  let contentElement;

  useEffect(() => {
    let setContentSize = () =>
      setContentHeight(contentElement && contentElement.clientHeight);
    setContentSize = debounce(setContentSize, 500);
    window && window.addEventListener('resize', setContentSize);

    return () => {
      window && window.removeEventListener('resize', setContentSize);
      setContentSize.cancel();
    };
  }, [contentElement]);

  const onToggleHandler = () => {
    setContentHeight(contentElement && contentElement.clientHeight);
    setIsContentShown(!isContentShown);
  };

  const onKeyDown = event => {
    if (event.keyCode === 13 || event.keyCode === 32) {
      onToggleHandler();
    }
  };

  const close = () => setIsContentShown(false);

  return (
    <S.Container appearance={appearance} isFullWidth={isFullWidth}>
      <S.StyledMain
        aria-expanded={isContentShown}
        isContentShown={isContentShown}
        onClick={!disabled ? onToggleHandler : undefined}
        onKeyDown={!disabled ? onKeyDown : undefined}
        role='button'
        tabIndex='0'
      >
        {main({ isShown: isContentShown, close })}
      </S.StyledMain>
      <S.StyledContent
        aria-hidden={!isContentShown}
        height={isContentShown ? contentHeight : 0}
      >
        <div ref={elem => (contentElement = elem)}>
          {typeof content === 'function'
            ? content({ isShown: isContentShown, close })
            : content}
        </div>
      </S.StyledContent>
    </S.Container>
  );
};

Collapse.propTypes = {
  /**
   * Changes the style of the collapse.
   */
  appearance: PropTypes.oneOf(Object.values(CollapseAppearance)),
  /**
    Default value of content elements before one of them becomes selected
    */
  content: PropTypes.func.isRequired,
  /**
   * Disable the collapse.
   */
  disabled: PropTypes.bool,
  /**
   * Display a wide dropdown.
   */
  isFullWidth: PropTypes.bool,
  /**
    Elements displayed in the collapse menu.
    */
  main: PropTypes.func.isRequired,
};

Collapse.defaultProps = {
  appearance: CollapseAppearance.CLASSIC,
  disabled: false,
  isFullWidth: false,
};
